import React, {useEffect, useState} from 'react';
import {ProductType} from '@wix/wixstores-client-core';
import {TextButton, TextButtonPriority, Tooltip} from 'wix-ui-tpa/cssVars';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {CodeInput} from './CodeInput/CodeInput';
import {getTooltipErrorMessageOptions} from './Coupon.helper';
import {classes} from './Coupon.st.css';
import {Tag} from '@wix/wix-ui-icons-common';
import {useControllerProps} from '../../../domain/controllers/ControllerContext';
import {useSettingsWithDefaults} from '../../../hooks/useSettingsWithDefaults';
import type {ISettingsParam} from '@wix/tpa-settings';
import settingsParams from '../../../components/cart/settingsParams';

export enum CouponDataHook {
  CouponRoot = 'Coupon.Root',
  EnterACouponButton = 'Coupon.EnterACouponButton',
  EnterACouponButtonDisabledTooltip = 'Coupon.EnterACodeButtonDisabledTooltip',
  CouponInputRoot = 'Coupon.CouponInputRoot',
}

export interface CouponProps {
  textButtonSettingKey?: ISettingsParam;
  placeholderTranslationKey?: string;
  applyButtonTranslationKey?: string;
}

export const Coupon = ({textButtonSettingKey, placeholderTranslationKey, applyButtonTranslationKey}: CouponProps) => {
  const {t} = useTranslation();
  const {
    cartModel,
    estimatedTotals,
    isGiftCardMixedCartEnabled,
    sendToggleCouponBi,
    applyCoupon,
    removeCoupon,
    couponError,
    isCouponUpdateInProgress,
    fixCouponCss,
    sideCartCouponAndNote,
  } = useControllerProps().cartStore;
  const appliedCoupon = estimatedTotals?.appliedCoupon || cartModel.appliedCoupon;
  const isCouponApplied = !!appliedCoupon;
  const shouldDisableCouponForGiftCard =
    isGiftCardMixedCartEnabled && cartModel.lineItemsTypes.includes(ProductType.GIFT_CARD);
  const {getSettingsWithDefaults} = useSettingsWithDefaults();
  const [isCollapsed, setIsCollapsed] = useState(!isCouponApplied);

  useEffect(() => {
    if (!isCouponApplied) {
      return;
    }
    setIsCollapsed(!isCouponApplied);
  }, [isCouponApplied]);

  const onTextButtonClicked = () => {
    isCollapsed && sendToggleCouponBi();
    setIsCollapsed(!isCollapsed);
  };

  const handleApplyCoupon = (code: string) => {
    return applyCoupon(code);
  };

  const handleRemoveCoupon = () => {
    return removeCoupon();
  };

  const getErrorText = () =>
    couponError ? t(`cart.errors.coupons.${couponError.code}`, getTooltipErrorMessageOptions(couponError)) : '';

  const textButton = (
    <TextButton
      data-hook={CouponDataHook.EnterACouponButton}
      priority={TextButtonPriority.primary}
      className={sideCartCouponAndNote ? classes.textButton : classes.textButtonOld}
      prefixIcon={<Tag />}
      aria-expanded={!isCollapsed}
      onClick={onTextButtonClicked}
      disabled={shouldDisableCouponForGiftCard}>
      {sideCartCouponAndNote
        ? getSettingsWithDefaults(textButtonSettingKey)
        : getSettingsWithDefaults(settingsParams.CART_ADD_PROMO_CODE_LINK)}
    </TextButton>
  );

  return (
    <div data-hook={CouponDataHook.CouponRoot} className={classes.couponRoot}>
      {shouldDisableCouponForGiftCard ? (
        <Tooltip
          data-hook={CouponDataHook.EnterACouponButtonDisabledTooltip}
          content={t('cart.errors.coupons.ERROR_CANT_PURCHASE_GC_MESSAGE')}
          minWidth={220}>
          {textButton}
        </Tooltip>
      ) : (
        textButton
      )}
      {!isCollapsed && (
        <div>
          <CodeInput
            dataHook={CouponDataHook.CouponInputRoot}
            applyText={sideCartCouponAndNote ? t(applyButtonTranslationKey) : t('cart.coupon_apply_button_label')}
            removeText={t('cart.remove_button')}
            appliedValue={appliedCoupon?.code}
            onApply={handleApplyCoupon}
            onRemove={handleRemoveCoupon}
            error={getErrorText()}
            placeholder={sideCartCouponAndNote ? t(placeholderTranslationKey) : t('cart.coupon_input_placeholder')}
            isLoading={isCouponUpdateInProgress}
            ariaLabel={t('cart.coupon_input_code_aria_label')}
            fixCouponCss={fixCouponCss}
          />
        </div>
      )}
    </div>
  );
};
